import { useAlert } from '../contexts/AlertContext';
import React, { useEffect, useState } from 'react';
import { BidSnapshot } from '../types/bid';
import { FinancePlan, HrCost, Ppe } from '../types/project';
import { projectApi } from '../mocks/projectApi';
import { emptyPpe, freelancerAmount, isCof, isFreelancer, isPm, ppeTotals, settlePpe } from '../lib/ppe';

/*
 * 재무계획 — admin PPE 「새 프로젝트 등록」(GRK_admin frontend/src/components/NewProjectModal.tsx)의 표를 **그대로** 옮겼다(2026-10-16).
 * 마크업 · 줄 · 셈 · 단위(만원)가 같다. 다른 점: by Consultant 줄이 없고, M/M 은 「투입경과」 창 대신 칸에 바로 넣는다.
 * 확정해도 admin 에 가지 않는다 — admin 반영은 수주 판정의 「PPE 반영」에서만.
 */

const formatCurrency = (value: number) => (isNaN(value) ? '-' : new Intl.NumberFormat('ko-KR').format(Math.round(value)));
const formatPercentage = (value: number) => (isNaN(value) ? '0.0%' : `${(value * 100).toFixed(1)}%`);
const noSpin = '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

/** admin EditableCell 과 같다 — 누르면 입력칸, 벗어나면 반영 */
const EditableCell: React.FC<{ value: number; onChange: (v: number) => void; readOnly?: boolean }> = ({ value, onChange, readOnly }) => {
  const [editing, setEditing] = useState(false);
  const [cur, setCur] = useState<string>(String(value));
  useEffect(() => setCur(String(value)), [value]);
  if (readOnly) return <span className="px-2 py-1 min-h-[20px] inline-block w-full text-right">{value ? formatCurrency(value) : <span className="text-gray-400">-</span>}</span>;
  return editing ? (
    <input type="number" value={cur} autoFocus onChange={(e) => setCur(e.target.value)}
      onBlur={() => { setEditing(false); onChange(Math.max(0, Number(cur) || 0)); }}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      placeholder="입력" className={`w-full bg-white border border-indigo-300 rounded px-2 py-1 text-xs text-right ${noSpin}`} />
  ) : (
    <span onClick={() => setEditing(true)} className="cursor-pointer hover:bg-gray-100 rounded px-2 py-1 min-h-[20px] inline-block w-full text-right">
      {value ? formatCurrency(value) : <span className="text-gray-400">입력</span>}
    </span>
  );
};

interface Props {
  plan: FinancePlan;
  /** 공고 스냅샷 — 배정예산 · 추정가격을 위 정보줄에 보인다. 직접 등록한 프로젝트는 null */
  bid?: BidSnapshot | null;
  onChange: (plan: FinancePlan) => void;
  /** 저장했으면 true */
  onSave: () => Promise<boolean>;
  onConfirm: () => void;
  /** 확정 해제 — 판정 전에만. 제안가를 고쳐 다시 확정한다 */
  onUnconfirm: () => void;
  /** 수주 · 실주 판정이 끝났으면 true — 그때는 되돌리지 않는다(PPE 가 admin 에 가 있다) */
  decided: boolean;
  saving: boolean;
}

function bidBudget(bid: BidSnapshot | null | undefined): { label: '배정예산' | '추정가격'; amount: number } | null {
  if (!bid) return null;
  const a = Number(bid.asignBdgtAmt || 0);
  if (a > 0) return { label: '배정예산', amount: a };
  const p = Number(bid.presmptPrce || 0);
  return p > 0 ? { label: '추정가격', amount: p } : null;
}

export const FinancePlanTab: React.FC<Props> = ({ plan, bid, onChange, onSave, onConfirm, onUnconfirm, decided, saving }) => {
  const readOnly = plan.status === '확정';
  const { showConfirm } = useAlert();
  const [savedJson, setSavedJson] = useState(() => JSON.stringify(plan));
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const dirty = !readOnly && JSON.stringify(plan) !== savedJson;
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const save = async () => {
    const snapshot = JSON.stringify(plan);
    if (await onSave()) {
      setSavedJson(snapshot);
      setSavedAt(new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }));
    }
  };
  const budget = bidBudget(bid);

  const projectData: Ppe = { ...emptyPpe(), ...(plan.ppe ?? {}) };
  const totals = ppeTotals(projectData);
  /** 바꿀 때마다 자동 칸을 다시 채우고, 제안가(원) = Revenue(만원) × 10,000 */
  const update = (next: Ppe) => {
    const p = settlePpe(next);
    onChange({ ...plan, ppe: p, proposalPrice: ppeTotals(p).revenue * 10000 });
  };

  // 옛 계획(PPE 없음) · 빈 계획 — 제안가(없으면 공고 예산)를 잔금에 넣어 시작한다. 저장은 「임시저장」에서
  useEffect(() => {
    if (readOnly || (plan.ppe && totals.revenue > 0)) return;
    const start = Math.round((plan.proposalPrice || budget?.amount || 0) / 10000);
    if (!plan.ppe || start > 0) update({ ...projectData, revenueItems: projectData.revenueItems.map((x) => (x.name === '잔금' ? { ...x, amount: start } : x)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.projectId, budget?.amount]);

  /* 인력원가 — admin 과 같은 사람 · 같은 값. 기간 시작 연도 기준(admin 과 같다) */
  const [employees, setEmployees] = useState<HrCost[]>([]);
  useEffect(() => {
    if (readOnly) return;
    projectApi.hrCosts(projectData.hrYear).then(setEmployees).catch(() => setEmployees([]));
  }, [projectData.hrYear, readOnly]);

  const setAt = <K extends 'revenueItems' | 'manpowerItems' | 'grossCostItems' | 'indirectOpexItems' | 'directOpexItems'>(key: K, i: number, patch: Partial<Ppe[K][number]>) =>
    update({ ...projectData, [key]: (projectData[key] as Ppe[K][number][]).map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const mmInput = (v: number, onSet: (n: number) => void) =>
    readOnly ? <span className="w-12 text-center text-xs text-gray-600">{v || 0}</span>
      : <input type="number" step="0.1" min={0} value={v || ''} placeholder="M/M" onChange={(e) => onSet(Math.max(0, Number(e.target.value) || 0))}
        className={`w-14 text-center bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs ${noSpin}`} />;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-4 font-sans text-xs">
        {/* 프로젝트 기본 정보 바 — admin 과 같다 */}
        <div className="mb-4 p-3 bg-slate-100 rounded-lg flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-600">기간:</span>
            {readOnly ? <span>{projectData.startDate || '-'} ~ {projectData.endDate || '-'}</span> : (
              <div className="flex items-center gap-1">
                <input type="date" value={projectData.startDate} onChange={(e) => update({ ...projectData, startDate: e.target.value })} className="bg-white rounded px-2 py-1 text-xs" />
                <span>~</span>
                <input type="date" value={projectData.endDate} onChange={(e) => update({ ...projectData, endDate: e.target.value })} className="bg-white rounded px-2 py-1 text-xs" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-600">기간(월):</span>
            <span className="text-blue-600 font-bold">{projectData.months}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-600">인건비율:</span>
            <span className="text-blue-600 font-bold">{formatPercentage(totals.manpower / (totals.revenue || 1))}</span>
            <span className="text-xs text-red-500">※ 65% 미만 권장</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-600">ECM:</span>
            <span className="text-red-500 font-bold">{formatCurrency(totals.ecm)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-600">Reserved:</span>
            <span className="text-red-500 font-bold">{formatCurrency(totals.reserved)}</span>
          </div>
          {budget && (
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-600">공고 {budget.label}:</span>
              <span className={`font-bold ${totals.revenue * 10000 > budget.amount ? 'text-red-600' : 'text-slate-700'}`}>{formatCurrency(budget.amount / 10000)}</span>
            </div>
          )}
        </div>

        <div className="mb-2 text-right text-xs text-gray-600">(단위 : 만원)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-700 text-white font-bold">
                <th className="p-2 w-28">Category</th>
                <th className="p-2 w-36">Item</th>
                <th className="p-2 w-40">Contents</th>
                <th className="p-2 w-20">기간/비율</th>
                <th className="p-2 w-24">금액</th>
                <th className="p-2 w-24">비고</th>
              </tr>
            </thead>
            <tbody>
              {/* ========== Revenue ========== */}
              <tr className="bg-slate-200 font-bold">
                <td className="p-2">Revenue</td>
                <td className="p-2"></td>
                <td className="p-2"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-right">{formatCurrency(totals.revenue)}</td>
                <td className="p-2 text-center text-xs text-gray-500">입금예정일</td>
              </tr>
              <tr className="bg-slate-100">
                <td className="p-2"></td>
                <td className="p-2 font-semibold">by Schedule</td>
                <td className="p-2"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-right font-semibold">{formatCurrency(totals.revenue)}</td>
                <td className="p-2"></td>
              </tr>
              {projectData.revenueItems.map((item, i) => (
                <tr key={`schedule-${i}`} className="hover:bg-gray-50">
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                  <td className="p-2">
                    {readOnly ? <span className="px-2 py-1">{item.name}</span> : (
                      <div className="flex items-center gap-2">
                        <input type="text" value={item.name} placeholder="항목명" onChange={(e) => setAt('revenueItems', i, { name: e.target.value })}
                          className="flex-1 bg-white border border-indigo-300 rounded px-2 py-1 text-xs" />
                        {projectData.revenueItems.length > 1 && (
                          <button onClick={() => update({ ...projectData, revenueItems: projectData.revenueItems.filter((_, k) => k !== i) })}
                            className="text-red-500 hover:text-red-700 text-lg font-bold" title="삭제">×</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-center">{formatPercentage(totals.revenue > 0 ? item.amount / totals.revenue : 0)}</td>
                  <td className="p-2 text-right"><EditableCell value={item.amount} onChange={(v) => setAt('revenueItems', i, { amount: v })} readOnly={readOnly} /></td>
                  <td className="p-2 text-center">
                    {readOnly ? <span>{item.dueDate || '-'}</span> : (
                      <input type="date" value={item.dueDate || ''} onChange={(e) => setAt('revenueItems', i, { dueDate: e.target.value })}
                        className="w-28 bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs" />
                    )}
                  </td>
                </tr>
              ))}
              {!readOnly && (
                <tr>
                  <td className="p-1" colSpan={6}>
                    <button
                      onClick={() => {
                        const used = projectData.revenueItems.map((x) => x.name);
                        const name = ['착수금', '중도금', '잔금', '추가금', '기타'].find((n) => !used.includes(n)) || `항목 ${projectData.revenueItems.length + 1}`;
                        update({ ...projectData, revenueItems: [...projectData.revenueItems, { name, ratio: 0, amount: 0, dueDate: '' }] });
                      }}
                      className="w-full py-1 text-indigo-600 hover:bg-indigo-50 rounded text-xs font-medium"
                    >+ Schedule 항목 추가</button>
                  </td>
                </tr>
              )}

              {/* ========== CoGs ========== */}
              <tr className="bg-slate-200 font-bold">
                <td className="p-2">CoGs</td>
                <td className="p-2"></td>
                <td className="p-2 text-xs text-gray-500">MM × 단가</td>
                <td className="p-2 text-center text-xs text-gray-500">M/M</td>
                <td className="p-2 text-right">{formatCurrency(totals.cogs + totals.manpower)}</td>
                <td className="p-2"></td>
              </tr>
              <tr className="bg-slate-100">
                <td className="p-2"></td>
                <td className="p-2 font-semibold">투입인건비</td>
                <td className="p-2 text-xs text-gray-500">이름 / M/M</td>
                <td className="p-2 text-center text-xs text-gray-500">인력원가</td>
                <td className="p-2 text-right font-semibold">{formatCurrency(totals.manpower)}</td>
                <td className="p-2 text-center text-xs text-gray-500"></td>
              </tr>
              {projectData.manpowerItems.map((item, i) => {
                const unitCost = item.unitCost || 0;
                return (
                  <tr key={`manpower-${i}`} className="hover:bg-gray-50">
                    <td className="p-2"></td>
                    <td className="p-2"></td>
                    <td className="p-2">
                      {readOnly ? <span>{item.name} / {item.mm || 0}</span> : (
                        <div className="flex items-center gap-1">
                          <input type="text" list={`employee-list-${i}`} value={item.name} placeholder="이름"
                            onChange={(e) => {
                              const emp = employees.find((x) => x.name === e.target.value && x.status === 'ACTIVE');
                              setAt('manpowerItems', i, emp
                                ? { name: emp.name, employeeId: emp.employeeId, position: emp.position, unitCost: emp.unitCost }
                                : { name: e.target.value, employeeId: null });
                            }}
                            className="flex-1 bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs min-w-0" />
                          <datalist id={`employee-list-${i}`}>
                            {employees.filter((x) => x.status === 'ACTIVE').map((x) => <option key={x.employeeId} value={x.name}>{x.name}</option>)}
                          </datalist>
                          {mmInput(item.mm, (v) => setAt('manpowerItems', i, { mm: v }))}
                          <button onClick={() => update({ ...projectData, manpowerItems: projectData.manpowerItems.filter((_, k) => k !== i) })}
                            className="text-red-500 hover:text-red-700 font-bold">×</button>
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {readOnly ? <span>{formatCurrency(unitCost)}</span> : (
                        <input type="number" step="100" value={unitCost || ''} placeholder="0"
                          onChange={(e) => setAt('manpowerItems', i, { unitCost: Math.max(0, parseFloat(e.target.value) || 0) })}
                          className={`w-16 text-center bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs ${noSpin}`} />
                      )}
                    </td>
                    <td className="p-2 text-right font-semibold">{formatCurrency(Math.round((item.mm || 0) * unitCost))}</td>
                    <td className="p-2 text-center"></td>
                  </tr>
                );
              })}
              {!readOnly && (
                <tr>
                  <td className="p-1" colSpan={6}>
                    <button onClick={() => update({ ...projectData, manpowerItems: [...projectData.manpowerItems, { name: '', employeeId: null, position: '', mm: 0, unitCost: 0 }] })}
                      className="w-full py-1 text-slate-600 hover:bg-slate-50 rounded text-xs font-medium">+ 인력 추가</button>
                  </td>
                </tr>
              )}

              <tr className="bg-slate-100">
                <td className="p-2"></td>
                <td className="p-2 font-semibold">프리랜서</td>
                <td className="p-2 text-xs text-gray-500">MM × 단가</td>
                <td className="p-2 text-center text-xs text-gray-500">M/M</td>
                <td className="p-2 text-right font-semibold">{formatCurrency(totals.cogs)}</td>
                <td className="p-2 text-center"></td>
              </tr>
              {projectData.grossCostItems.map((item, i) => {
                const free = isFreelancer(item.name);
                return (
                  <tr key={`cogs-${i}`} className="hover:bg-gray-50">
                    <td className="p-2"></td>
                    <td className="p-2"></td>
                    <td className="p-2">{item.name}</td>
                    <td className="p-2 text-center">{free && mmInput(item.mm ?? 0, (v) => setAt('grossCostItems', i, { mm: v }))}</td>
                    <td className="p-2 text-right">
                      {free ? formatCurrency(freelancerAmount(item)) : <EditableCell value={item.amount} onChange={(v) => setAt('grossCostItems', i, { amount: v })} readOnly={readOnly} />}
                    </td>
                    <td className="p-2 text-center"></td>
                  </tr>
                );
              })}

              <tr className="bg-slate-200 font-bold">
                <td className="p-2">Gross Margin</td>
                <td className="p-2"></td>
                <td className="p-2"></td>
                <td className="p-2 text-center">{formatPercentage(totals.grossMargin / (totals.revenue || 1))}</td>
                <td className="p-2 text-right">{formatCurrency(totals.grossMargin)}</td>
                <td className="p-2"></td>
              </tr>

              {/* ========== SG&A ========== */}
              <tr className="bg-slate-200 font-bold">
                <td className="p-2">SG&amp;A</td>
                <td className="p-2"></td>
                <td className="p-2"></td>
                <td className="p-2 text-center">{formatPercentage((totals.indirect + totals.direct) / (totals.revenue || 1))}</td>
                <td className="p-2 text-right">{formatCurrency(totals.indirect + totals.direct)}</td>
                <td className="p-2"></td>
              </tr>
              {([
                ['Indirect Opex', 'indirectOpexItems', totals.indirect, isCof, 'costOfFinanceRate', totals.costOfFinance, '※ 10% 기본'],
                ['Direct Opex', 'directOpexItems', totals.direct, isPm, 'projectManagementRate', totals.projectManagement, '※ 3% 기본'],
              ] as const).map(([label, key, total, isAutoRow, rateKey, autoAmount, note]) => (
                <React.Fragment key={key}>
                  <tr className="bg-slate-100">
                    <td className="p-2"></td>
                    <td className="p-2 font-semibold">{label}</td>
                    <td className="p-2"></td>
                    <td className="p-2 text-center">{formatPercentage(total / (totals.revenue || 1))}</td>
                    <td className="p-2 text-right">{formatCurrency(total)}</td>
                    <td className="p-2"></td>
                  </tr>
                  {projectData[key].map((item, i) => (
                    <tr key={`${key}-${i}`} className="hover:bg-gray-50">
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2">{item.name}</td>
                      <td className="p-2 text-center">
                        {isAutoRow(item.name) ? (
                          readOnly ? formatPercentage(projectData[rateKey]) : (
                            <div className="flex items-center justify-center">
                              <input type="number" step="0.1" value={(projectData[rateKey] * 100).toFixed(1)}
                                onChange={(e) => update({ ...projectData, [rateKey]: Math.min(Math.max(Number(e.target.value) / 100, 0), 1) })}
                                className={`w-12 text-right bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs ${noSpin}`} />
                              <span className="ml-0.5 text-xs">%</span>
                            </div>
                          )
                        ) : formatPercentage((item.amount || 0) / (totals.revenue || 1))}
                      </td>
                      <td className="p-2 text-right">
                        {isAutoRow(item.name) ? formatCurrency(autoAmount)
                          : <EditableCell value={item.amount} onChange={(v) => setAt(key, i, { amount: v })} readOnly={readOnly} />}
                      </td>
                      <td className="p-2 text-xs text-red-400">{isAutoRow(item.name) && note}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}

              <tr className="font-bold bg-slate-200">
                <td className="p-2">Operation Margin</td>
                <td className="p-2"></td>
                <td className="p-2"></td>
                <td className="p-2 text-center">{formatPercentage(totals.operation / (totals.revenue || 1))}</td>
                <td className="p-2 text-right">{formatCurrency(totals.operation)}</td>
                <td className="p-2"></td>
              </tr>
              <tr className="bg-slate-200">
                <td className="p-2"></td>
                <td className="p-2">Income Tax (Est. 20%)</td>
                <td className="p-2"></td>
                <td className="p-2 text-center">{formatPercentage(totals.tax / (totals.revenue || 1))}</td>
                <td className="p-2 text-right">{formatCurrency(totals.tax)}</td>
                <td className="p-2"></td>
              </tr>
              <tr className="font-bold bg-slate-800 text-white">
                <td className="p-2">Net Profit</td>
                <td className="p-2"></td>
                <td className="p-2"></td>
                <td className="p-2 text-center">{formatPercentage(totals.net / (totals.revenue || 1))}</td>
                <td className="p-2 text-right">{formatCurrency(totals.net)}</td>
                <td className="p-2 text-xs text-red-400">※ EP 10% 미만 불가</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {readOnly ? (
          <>
            <span className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">확정됨</span>
            <button
              onClick={async () => await showConfirm('확정을 해제해 다시 고칠 수 있게 합니다. 제안서로 넘긴 단계는 그대로 두고, 프로젝트에 둔 제안가 사본만 지웁니다. 계속할까요?') && onUnconfirm()}
              disabled={saving || decided}
              title={decided ? '판정이 끝난 건은 되돌리지 않습니다 — PPE 가 admin 에 가 있습니다' : '제안가를 고쳐 다시 확정합니다'}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? '해제 중...' : '확정 해제'}
            </button>
          </>
        ) : (
          <>
            <span className={`text-xs ${dirty ? 'text-amber-600' : 'text-slate-400'}`}>
              {dirty ? '저장 안 한 변경이 있습니다' : savedAt ? `${savedAt} 저장했습니다` : ''}
            </span>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {saving ? '저장 중...' : '임시저장'}
            </button>
            <button
              onClick={async () => await showConfirm(`제안가 ${formatCurrency(totals.revenue)}만원으로 확정하고 제안서로 넘깁니다.\n확정 뒤에는 고칠 수 없습니다. 계속할까요?`) && onConfirm()}
              disabled={saving || projectData.manpowerItems.length === 0 || totals.revenue <= 0}
              title={projectData.manpowerItems.length === 0 ? '투입인건비를 한 줄 이상 잡아야 확정할 수 있습니다' : totals.revenue <= 0 ? 'Revenue 를 넣어야 확정할 수 있습니다' : undefined}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              확정 · 제안서로
            </button>
          </>
        )}
      </div>
    </div>
  );
};
